import moment from "moment";
import { FunctionComponent, useEffect, useState } from "react";
import { GetOrderLogsApi } from "../../../../../service/repos/order_repo";
import { OrderLogType } from "../../../../../utils/enums";
import LoadingWidget from "../../../../components/loading_spinner";
import Pagination from "../../../../new_components/common/pagination";
export interface OrderLogModel {
  id: string;
  operation: String;
  user_id: string;
  order_id: string;
  created_at: string;
  user: string;
  log_type?: string;
  params?: any;
}
interface ActionHistoryProps {
  order_id: string;
  log_type?: string;
}

const ActionHistory: FunctionComponent<ActionHistoryProps> = (props) => {
  const [logs, setLogs] = useState<OrderLogModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [count, setCount] = useState(10);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    GetOrderLogsApi({
      order_id: props.order_id,
      page,
      count,
      log_type: props.log_type,
    })
      .then((res) => {
        if (res.success) {
          setLogs(res.data.logs);

          if (res.data.metadata[0]) {
            setTotal(res.data.metadata[0].total);
          }
        }
        setLoading(false);
      })
      .catch((e) => {
        setLoading(false);
      });
  }, [page]);

  return (
    <div className=" ">
      <div className="flex flex-col justify-between h-full">
        <div
          id="history_wrap"
          className="overflow-auto p-1 bg-gray-100"
          style={{ height: "88vh" }}
        >
          {logs.length === 0 && (
            <p className="text-center my-10">No History!</p>
          )}
          {logs.map((log, i) => (
            <div key={i} className="  mb-2 shadow rounded bg-white p-1">
              {log.log_type ? (
                getOrderLogTemplate(log)
              ) : (
                <div>
                  <b>{log.user}</b>
                  <p className="text-sm"> {log.operation}</p>
                  <div className="flex justify-end items-end py-2">
                    <span className="text-xs italic">
                      {moment(log.created_at).format("lll")}
                    </span>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
        <Pagination
          page={page}
          total={total}
          count={count}
          itemCount={logs.length}
          onChange={(page: number) => setPage(page)}
        />
      </div>
      <LoadingWidget loading={loading} />
    </div>
  );
};

export default ActionHistory;

const isIsoDate = (v: any) =>
  typeof v === "string" && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(v);

const prettyField = (key: string) =>
  key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const renderLogValue = (value: any): any => {
  if (value === null || value === undefined || value === "") return "-";
  if (isIsoDate(value)) return moment(value).format("ll");
  if (Array.isArray(value)) {
    if (value.length === 0) return "-";
    const summary = value
      .map((el: any) => {
        if (el && typeof el === "object") {
          const name = el.name ?? el.item?.name ?? el.firstName;
          const qty = el.qty ?? el.amount;
          if (name) return qty !== undefined ? `${name} × ${qty}` : `${name}`;
          return null;
        }
        return String(el);
      })
      .filter((x: any) => x !== null);
    return summary.length ? summary.join(", ") : `${value.length} item(s)`;
  }
  if (typeof value === "object") {
    const entries = Object.entries(value).filter(
      ([k]) => k !== "_id" && k !== "id",
    );
    if (!entries.length) return "-";
    return (
      <div className="space-y-0.5 text-left">
        {entries.map(([k, v]) => (
          <div key={k}>
            <span className="font-medium">{prettyField(k)}:</span>{" "}
            {v && typeof v === "object"
              ? isIsoDate(v)
                ? moment(v as any).format("ll")
                : JSON.stringify(v)
              : String(v ?? "-")}
          </div>
        ))}
      </div>
    );
  }
  return String(value);
};

const getOrderLogTemplate = (log: OrderLogModel) => {
  switch (log.log_type) {
    case OrderLogType.created:
      return (
        <>
          <p className="text-sm">
            <b>{log.user}</b> has created the order #{" "}
            <b className="text-blue-500">{log.params.order_id}</b>
          </p>
          <div className="flex justify-end items-end py-1">
            <span className="text-xs italic">
              {moment(log.created_at).format("lll")}
            </span>
          </div>
        </>
      );
    case OrderLogType.order_approved:
      return (
        <>
          <p className="text-sm">
            <b>{log.params?.approved_by_name || log.user}</b> has{" "}
            <span className="text-green-600 font-semibold">Approved </span>
            the order.
          </p>
          <div className="flex justify-end items-end py-1">
            <span className="text-xs italic">
              {moment(log.params?.approved_at || log.created_at).format("lll")}
            </span>
          </div>
        </>
      );
    case OrderLogType.warehouse_assigned:
      return (
        <>
          <p className="text-sm">
            <b>{log.params?.assigned_by_name || log.user}</b>{" "}
            <span className="text-blue-600 font-semibold">Assigned </span>
            warehouse{" "}
            <b className="text-blue-500">{log.params?.warehouse_name || "-"}</b>
            {log.params?.next_status ? (
              <>
                {" "}
                (status &rarr;{" "}
                <span className="italic">{log.params.next_status}</span>)
              </>
            ) : null}
            .
          </p>
          <div className="flex justify-end items-end py-1">
            <span className="text-xs italic">
              {moment(log.params?.assigned_at || log.created_at).format("lll")}
            </span>
          </div>
        </>
      );
    case OrderLogType.outward_tracking_pending:
      return (
        <>
          <p className="text-sm">
            <b>{log.params?.acted_by_name || log.user}</b> moved outward to{" "}
            <span className="text-yellow-600 font-semibold">
              Tracking ID Pending
            </span>
            .
          </p>
          <table className="border rounded w-full overflow-auto text-xs">
            <tbody>
              <tr className="border-b">
                <td align="left" className="px-2 py-1 font-semibold">
                  Logistic partner
                </td>
                <td align="left" className="px-2 py-1">
                  {log.params?.logistic_partner || "-"}
                </td>
              </tr>
              <tr className="border-b">
                <td align="left" className="px-2 py-1 font-semibold">
                  Tracking ID
                </td>
                <td align="left" className="px-2 py-1 italic text-gray-500">
                  pending
                </td>
              </tr>
              {log.params?.from_warehouse_name ? (
                <tr className="border-b">
                  <td align="left" className="px-2 py-1 font-semibold">
                    From warehouse
                  </td>
                  <td align="left" className="px-2 py-1">
                    {log.params.from_warehouse_name}
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
          <div className="flex justify-end items-end py-1">
            <span className="text-xs italic">
              {moment(log.params?.acted_at || log.created_at).format("lll")}
            </span>
          </div>
        </>
      );
    case OrderLogType.outward_dispatched:
      return (
        <>
          <p className="text-sm">
            <b>{log.params?.acted_by_name || log.user}</b> marked outward as{" "}
            <span className="text-green-600 font-semibold">Dispatched</span>.
          </p>
          <table className="border rounded w-full overflow-auto text-xs">
            <tbody>
              <tr className="border-b">
                <td align="left" className="px-2 py-1 font-semibold">
                  Logistic partner
                </td>
                <td align="left" className="px-2 py-1">
                  {log.params?.logistic_partner || "-"}
                </td>
              </tr>
              <tr className="border-b">
                <td align="left" className="px-2 py-1 font-semibold">
                  Tracking ID
                </td>
                <td align="left" className="px-2 py-1">
                  {log.params?.tracking_id || "-"}
                </td>
              </tr>
              {log.params?.from_warehouse_name ? (
                <tr className="border-b">
                  <td align="left" className="px-2 py-1 font-semibold">
                    From warehouse
                  </td>
                  <td align="left" className="px-2 py-1">
                    {log.params.from_warehouse_name}
                  </td>
                </tr>
              ) : null}
              {log.params?.dispatched_date ? (
                <tr className="border-b">
                  <td align="left" className="px-2 py-1 font-semibold">
                    Dispatched at
                  </td>
                  <td align="left" className="px-2 py-1">
                    {moment(log.params.dispatched_date).format("lll")}
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
          <div className="flex justify-end items-end py-1">
            <span className="text-xs italic">
              {moment(log.params?.acted_at || log.created_at).format("lll")}
            </span>
          </div>
        </>
      );
    case OrderLogType.updated:
      return (
        <>
          <p className="text-sm">
            <b>{log.user}</b> has{" "}
            <span className="text-yellow-500 font-semibold">Updated </span> the
            Order details.
          </p>
          <table className="border rounded w-full overflow-auto text-xs">
            <thead className="border rounded">
              <tr>
                <th className="">Field</th>
                <th>Previous value</th>
                <th>New value</th>
              </tr>
            </thead>
            <tbody>
              {Object.keys(log.params.new_value).map((l, i) => (
                <tr key={i} className="border-b">
                  <td align="center">{prettyField(l)}</td>
                  <td align="center" className=" bg-red-100 text-red-800">
                    {renderLogValue(log.params.pre_value[l])}
                  </td>
                  <td align="center" className="bg-green-100 text-green-800">
                    {renderLogValue(log.params.new_value[l])}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>{" "}
          <div className="flex justify-end items-end py-1">
            <span className="text-xs italic">
              {moment(log.created_at).format("lll")}
            </span>
          </div>
        </>
      );
    case OrderLogType.item_added:
      return (
        <>
          <p className="text-sm">
            <b>{log.user}</b> has{" "}
            <span className="text-green-500 font-semibold">Added </span> product
            or services.
          </p>
          <table className="border rounded w-full overflow-auto text-xs">
            <thead className="border rounded">
              <tr>
                <th className="">Product/Service</th>
                <th>Qty</th>
                <th>Price</th>
                <th>Del Qty</th>
                <th>Ins Qty</th>
                <th>Ret Qty</th>
                <th>Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b bg-green-100 text-green-800">
                <td align="center">{log.params.item.name}</td>
                <td align="center">{log.params.item.qty}</td>
                <td align="center">{log.params.item.price}</td>
                <td align="center">{log.params.item.delivered_qty}</td>
                <td align="center">{log.params.item.installed_qty}</td>
                <td align="center">{log.params.item.returned_qty}</td>
                <td align="center">
                  {log.params.item.price * log.params.item.qty}
                </td>
              </tr>
            </tbody>
          </table>{" "}
          <div className="flex justify-end items-end py-1">
            <span className="text-xs italic">
              {moment(log.created_at).format("lll")}
            </span>
          </div>
        </>
      );
    case OrderLogType.item_update:
      return (
        <>
          <p className="text-sm">
            <b>{log.user}</b> has{" "}
            <span className="text-yellow-500 font-semibold">Updated </span>{" "}
            product or services.
          </p>
          <table className="border rounded w-full overflow-auto text-xs">
            <thead className="border rounded">
              <tr>
                <th className=""> </th>
                <th className="">Product/Service</th>
                <th>Qty</th>
                <th>Price</th>
                <th>Del Qty</th>
                <th>Ins Qty</th>
                <th>Ret Qty</th>
                <th>Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b bg-red-100 text-red-800">
                <td align="left">- Previous</td>
                <td align="center">{log.params.pre_value.name}</td>
                <td align="center">{log.params.pre_value.qty}</td>
                <td align="center">{log.params.pre_value.price}</td>
                <td align="center">{log.params.pre_value.delivered_qty}</td>
                <td align="center">{log.params.pre_value.installed_qty}</td>
                <td align="center">{log.params.pre_value.returned_qty}</td>
                <td align="center">
                  {log.params.pre_value.price * log.params.pre_value.qty}
                </td>
              </tr>
              <tr className="border-b bg-green-100 text-green-800">
                <td align="left">+ New</td>
                <td align="center">{log.params.new_value.name}</td>
                <td align="center">{log.params.new_value.qty}</td>
                <td align="center">{log.params.new_value.price}</td>
                <td align="center">{log.params.new_value.delivered_qty}</td>
                <td align="center">{log.params.new_value.installed_qty}</td>
                <td align="center">{log.params.new_value.returned_qty}</td>
                <td align="center">
                  {log.params.new_value.price * log.params.new_value.qty}
                </td>
              </tr>
            </tbody>
          </table>{" "}
          <div className="flex justify-end items-end py-1">
            <span className="text-xs italic">
              {moment(log.created_at).format("lll")}
            </span>
          </div>
        </>
      );
    case OrderLogType.item_deleted:
      return (
        <>
          <p className="text-sm">
            <b>{log.user}</b> has{" "}
            <span className="text-red-500 font-semibold">Removed </span> product
            or services.
          </p>
          <table className="border rounded w-full overflow-auto text-xs">
            <thead className="border rounded">
              <tr>
                <th className="">Product/Service</th>
                <th>Qty</th>
                <th>Price</th>
                <th>Del Qty</th>
                <th>Ins Qty</th>
                <th>Ret Qty</th>
                <th>Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b   bg-red-100 text-red-800">
                <td align="center">{log.params.item.name}</td>
                <td align="center">{log.params.item.qty}</td>
                <td align="center">{log.params.item.price}</td>
                <td align="center">{log.params.item.delivered_qty}</td>
                <td align="center">{log.params.item.installed_qty}</td>
                <td align="center">{log.params.item.returned_qty}</td>
                <td align="center">
                  {log.params.item.price * log.params.item.qty}
                </td>
              </tr>
            </tbody>
          </table>{" "}
          <div className="flex justify-end items-end py-1">
            <span className="text-xs italic">
              {moment(log.created_at).format("lll")}
            </span>
          </div>
        </>
      );
    case OrderLogType.extra_added:
      return (
        <>
          <p className="text-sm">
            <b>{log.user}</b> has{" "}
            <span className="text-green-500 font-semibold">Added </span>{" "}
            Consumables.
          </p>
          <table className="border rounded w-full overflow-auto text-xs">
            <thead className="border rounded">
              <tr>
                <th className="">Category</th>
                <th>Description</th>
                <th>Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b bg-green-100 text-green-800">
                <td align="center">{log.params.extra.category}</td>
                <td align="center">{log.params.extra.name}</td>
                <td align="center">{log.params.extra.amount}</td>
              </tr>
            </tbody>
          </table>{" "}
          <div className="flex justify-end items-end py-1">
            <span className="text-xs italic">
              {moment(log.created_at).format("lll")}
            </span>
          </div>
        </>
      );
    case OrderLogType.extra_update:
      return (
        <>
          <p className="text-sm">
            <b>{log.user}</b> has{" "}
            <span className="text-yellow-500 font-semibold">Updated </span>{" "}
            Extras.
          </p>
          <table className="border rounded w-full overflow-auto text-xs">
            <thead className="border rounded">
              <tr>
                <th className=""> </th>
                <th className="">Category</th>
                <th>Description</th>
                <th>Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b bg-red-100 text-red-800">
                <td align="left">- Previous</td>
                <td align="center">{log.params.pre_value.category}</td>
                <td align="center">{log.params.pre_value.name}</td>
                <td align="center">{log.params.pre_value.amount}</td>
              </tr>
              <tr className="border-b bg-green-100 text-green-800">
                <td align="left">+ New</td>
                <td align="center">{log.params.new_value.category}</td>
                <td align="center">{log.params.new_value.name}</td>
                <td align="center">{log.params.new_value.amount}</td>
              </tr>
            </tbody>
          </table>{" "}
          <div className="flex justify-end items-end py-1">
            <span className="text-xs italic">
              {moment(log.created_at).format("lll")}
            </span>
          </div>
        </>
      );
    case OrderLogType.extra_deleted:
      return (
        <>
          <p className="text-sm">
            <b>{log.user}</b> has{" "}
            <span className="text-red-500 font-semibold">Removed </span> Extras.
          </p>
          <table className="border rounded w-full overflow-auto text-xs">
            <thead className="border rounded">
              <tr>
                <th className="">Category</th>
                <th>Description</th>
                <th>Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b   bg-red-100 text-red-800">
                <td align="center">{log.params.extra.category}</td>
                <td align="center">{log.params.extra.name}</td>
                <td align="center">{log.params.extra.amount}</td>
              </tr>
            </tbody>
          </table>{" "}
          <div className="flex justify-end items-end py-1">
            <span className="text-xs italic">
              {moment(log.created_at).format("lll")}
            </span>
          </div>
        </>
      );
    case OrderLogType.discount_added:
      return (
        <>
          <p className="text-sm">
            <b>{log.user}</b> has{" "}
            <span className="text-green-500 font-semibold">Added </span>{" "}
            Discount.
          </p>
          <table className="border rounded w-full overflow-auto text-xs">
            <thead className="border rounded">
              <tr>
                <th>Description</th>
                <th>Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b bg-green-100 text-green-800">
                <td align="center">{log.params.discount.name}</td>
                <td align="center">{log.params.discount.amount}</td>
              </tr>
            </tbody>
          </table>{" "}
          <div className="flex justify-end items-end py-1">
            <span className="text-xs italic">
              {moment(log.created_at).format("lll")}
            </span>
          </div>
        </>
      );
    case OrderLogType.discount_update:
      return (
        <>
          <p className="text-sm">
            <b>{log.user}</b> has{" "}
            <span className="text-yellow-500 font-semibold">Updated </span>{" "}
            Discount.
          </p>
          <table className="border rounded w-full overflow-auto text-xs">
            <thead className="border rounded">
              <tr>
                <th className=""> </th>
                <th>Description</th>
                <th>Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b bg-red-100 text-red-800">
                <td align="left">- Previous</td>

                <td align="center">{log.params.pre_value.name}</td>
                <td align="center">{log.params.pre_value.amount}</td>
              </tr>
              <tr className="border-b bg-green-100 text-green-800">
                <td align="left">+ New</td>

                <td align="center">{log.params.new_value.name}</td>
                <td align="center">{log.params.new_value.amount}</td>
              </tr>
            </tbody>
          </table>{" "}
          <div className="flex justify-end items-end py-1">
            <span className="text-xs italic">
              {moment(log.created_at).format("lll")}
            </span>
          </div>
        </>
      );
    case OrderLogType.discount_deleted:
      return (
        <>
          <p className="text-sm">
            <b>{log.user}</b> has{" "}
            <span className="text-red-500 font-semibold">Removed </span>{" "}
            Discount.
          </p>
          <table className="border rounded w-full overflow-auto text-xs">
            <thead className="border rounded">
              <tr>
                <th>Description</th>
                <th>Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b   bg-red-100 text-red-800">
                <td align="center">{log.params.discount.name}</td>
                <td align="center">{log.params.discount.amount}</td>
              </tr>
            </tbody>
          </table>{" "}
          <div className="flex justify-end items-end py-1">
            <span className="text-xs italic">
              {moment(log.created_at).format("lll")}
            </span>
          </div>
        </>
      );
    case OrderLogType.commented:
      return (
        <>
          <p className="text-sm">
            <b>{log.user}</b> has{" "}
            <span className="text-green-500 font-semibold">Added </span>{" "}
            comment.{" "}
          </p>
          <p className="text-xs italic">{log.params.comment}</p>
          <div className="flex justify-end items-end py-1">
            <span className="text-xs italic">
              {moment(log.created_at).format("lll")}
            </span>
          </div>
        </>
      );
    case OrderLogType.comment_deleted:
      return (
        <>
          <p className="text-sm">
            <b>{log.user}</b> has{" "}
            <span className="text-red-500 font-semibold">Deleted </span> comment
          </p>
          <p className="text-xs italic">{log.params.comment}</p>
          <div className="flex justify-end items-end py-1">
            <span className="text-xs italic">
              {moment(log.created_at).format("lll")}
            </span>
          </div>
        </>
      );
    case OrderLogType.created_complaint:
      return (
        <>
          <p className="text-sm">
            <b>{log.user}</b> has created complaint with ID :{" "}
            <b className="text-blue-500">{log.params.complaint_id}</b>
          </p>
          <div className="flex justify-end items-end py-1">
            <span className="text-xs italic">
              {moment(log.created_at).format("lll")}
            </span>
          </div>
        </>
      );
    case OrderLogType.customer_update:
      return (
        <>
          <p className="text-sm">
            <b>{log.user}</b> has{" "}
            <span className="text-yellow-500 font-semibold">Updated </span>{" "}
            Billing Profile.
          </p>
          <table className="border rounded w-full overflow-auto text-xs">
            <thead className="border rounded">
              <tr>
                <th className=""> </th>
                <th>First Name</th>
                <th>Last Name</th>
                <th>Mobile</th>
                <th>Alt Mobile</th>
                <th>Email</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b bg-red-100 text-red-800">
                <td align="left">- Previous</td>

                <td align="center">{log.params.pre_value.firstName}</td>
                <td align="center">{log.params.pre_value.lastName}</td>
                <td align="center">{log.params.pre_value.mobile}</td>
                <td align="center">{log.params.pre_value.alt_mobile}</td>
                <td align="center">{log.params.pre_value.email}</td>
              </tr>
              <tr className="border-b bg-green-100 text-green-800">
                <td align="left">+ New</td>

                <td align="center">{log.params.new_value.firstName}</td>
                <td align="center">{log.params.new_value.lastName}</td>
                <td align="center">{log.params.new_value.mobile}</td>
                <td align="center">{log.params.new_value.alt_mobile}</td>
                <td align="center">{log.params.new_value.email}</td>
              </tr>
            </tbody>
          </table>{" "}
          <div className="flex justify-end items-end py-1">
            <span className="text-xs italic">
              {moment(log.created_at).format("lll")}
            </span>
          </div>
        </>
      );
    case OrderLogType.address_update: {
      const profileLabel =
        log.params?.profile_type === "delivery" ? "Delivery" : "Billing";
      const pre = log.params?.pre_value || {};
      const next = log.params?.new_value || {};
      const preAddr = pre.address || pre;
      const newAddr = next.address || next;
      const addressRows: { label: string; key: string }[] = [
        { label: "Line 1", key: "line1" },
        { label: "Line 2", key: "line2" },
        { label: "Line 3", key: "line3" },
        { label: "City", key: "city" },
        { label: "State", key: "state" },
        { label: "Country", key: "country" },
        { label: "Pincode", key: "pincode" },
      ];
      return (
        <>
          <p className="text-sm">
            <b>{log.user}</b> has{" "}
            <span className="text-yellow-500 font-semibold">Updated </span>{" "}
            {profileLabel} Address.
          </p>
          <table className="border rounded w-full overflow-auto text-xs">
            <thead className="border rounded">
              <tr>
                <th className="text-left px-2 py-1">Field</th>
                <th className="text-left px-2 py-1 text-red-600">
                  Previous value
                </th>
                <th className="text-left px-2 py-1 text-green-600">
                  New value
                </th>
              </tr>
            </thead>
            <tbody>
              {addressRows.map((row) => (
                <tr key={row.key} className="border-b">
                  <td className="px-2 py-1 font-medium">{row.label}</td>
                  <td className="px-2 py-1 bg-red-50 text-red-800">
                    {(preAddr as any)[row.key] || "-"}
                  </td>
                  <td className="px-2 py-1 bg-green-50 text-green-800">
                    {(newAddr as any)[row.key] || "-"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>{" "}
          <div className="flex justify-end items-end py-1">
            <span className="text-xs italic">
              {moment(log.created_at).format("lll")}
            </span>
          </div>
        </>
      );
    }

    case OrderLogType.payment_pending:
      return (
        <>
          <p className="text-sm">
            <b>{log.user}</b> has{" "}
            <span className="text-yellow-500 font-semibold">Updated </span>{" "}
            payment.
          </p>
          <table className="border rounded w-full overflow-auto text-xs">
            <thead className="border rounded">
              <tr>
                <th className=""> </th>
                <th>Txn Id</th>
                <th>Mode</th>
                <th>Status</th>

                <th>Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b bg-red-100 text-red-800">
                <td align="left">- Previous</td>

                <td align="center">{log.params.pre_value.txn_id}</td>
                <td align="center">{log.params.pre_value.mode}</td>
                <td align="center">{log.params.pre_value.status}</td>
                <td align="center">{log.params.pre_value.amount}</td>
              </tr>
              <tr className="border-b bg-green-100 text-green-800">
                <td align="left">+ New</td>

                <td align="center">{log.params.new_value.txn_id}</td>
                <td align="center">{log.params.new_value.mode}</td>
                <td align="center">{log.params.new_value.status}</td>
                <td align="center">{log.params.new_value.amount}</td>
              </tr>
            </tbody>
          </table>{" "}
          <div className="flex justify-end items-end py-1">
            <span className="text-xs italic">
              {moment(log.created_at).format("lll")}
            </span>
          </div>
        </>
      );

    case OrderLogType.payment_approved:
      return (
        <>
          <p className="text-sm">
            <b>{log.user}</b> has{" "}
            <span className="text-green-500 font-semibold">Approved </span>{" "}
            payment.
          </p>
          <table className="border rounded w-full overflow-auto text-xs">
            <thead className="border rounded">
              <tr>
                <th className=""> </th>
                <th>Txn Id</th>
                <th>Mode</th>
                <th>Status</th>

                <th>Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b bg-red-100 text-red-800">
                <td align="left">- Previous</td>

                <td align="center">{log.params.pre_value.txn_id}</td>
                <td align="center">{log.params.pre_value.mode}</td>
                <td align="center">{log.params.pre_value.status}</td>
                <td align="center">{log.params.pre_value.amount}</td>
              </tr>
              <tr className="border-b bg-green-100 text-green-800">
                <td align="left">+ New</td>

                <td align="center">{log.params.new_value.txn_id}</td>
                <td align="center">{log.params.new_value.mode}</td>
                <td align="center">{log.params.new_value.status}</td>
                <td align="center">{log.params.new_value.amount}</td>
              </tr>
            </tbody>
          </table>{" "}
          <div className="flex justify-end items-end py-1">
            <span className="text-xs italic">
              {moment(log.created_at).format("lll")}
            </span>
          </div>
        </>
      );
    case OrderLogType.payment_rejected:
      return (
        <>
          <p className="text-sm">
            <b>{log.user}</b> has{" "}
            <span className="text-red-500 font-semibold">Rejected </span>{" "}
            payment.
          </p>
          <table className="border rounded w-full overflow-auto text-xs">
            <thead className="border rounded">
              <tr>
                <th className=""> </th>
                <th>Txn Id</th>
                <th>Mode</th>
                <th>Status</th>

                <th>Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b bg-red-100 text-red-800">
                <td align="left">- Previous</td>

                <td align="center">{log.params.pre_value.txn_id}</td>
                <td align="center">{log.params.pre_value.mode}</td>
                <td align="center">{log.params.pre_value.status}</td>
                <td align="center">{log.params.pre_value.amount}</td>
              </tr>
              <tr className="border-b bg-green-100 text-green-800">
                <td align="left">+ New</td>

                <td align="center">{log.params.new_value.txn_id}</td>
                <td align="center">{log.params.new_value.mode}</td>
                <td align="center">{log.params.new_value.status}</td>
                <td align="center">{log.params.new_value.amount}</td>
              </tr>
            </tbody>
          </table>{" "}
          <div className="flex justify-end items-end py-1">
            <span className="text-xs italic">
              {moment(log.created_at).format("lll")}
            </span>
          </div>
        </>
      );

    case OrderLogType.payment_added:
      return (
        <>
          <p className="text-sm">
            <b>{log.user}</b> has{" "}
            <span className="text-green-500 font-semibold">Added </span>{" "}
            payment.
          </p>
          <table className="border rounded w-full overflow-auto text-xs">
            <thead className="border rounded">
              <tr>
                <th> </th>
                <th>Txn Id</th>
                <th>Mode</th>
                <th>Status</th>

                <th>Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b bg-green-100 text-green-800">
                <td align="left">+ New</td>

                <td align="center">{log.params.payment.txn_id}</td>
                <td align="center">{log.params.payment.mode}</td>
                <td align="center">{log.params.payment.status}</td>
                <td align="center">{log.params.payment.amount}</td>
              </tr>
            </tbody>
          </table>{" "}
          <div className="flex justify-end items-end py-1">
            <span className="text-xs italic">
              {moment(log.created_at).format("lll")}
            </span>
          </div>
        </>
      );

    case OrderLogType.payment_attachment_added:
      return (
        <>
          <p className="text-sm">
            <b>{log.user}</b> has{" "}
            <span className="text-green-500 font-semibold">Uploaded </span>{" "}
            payment attachments.
          </p>

          <div className="flex justify-end items-end py-1">
            <span className="text-xs italic">
              {moment(log.created_at).format("lll")}
            </span>
          </div>
        </>
      );
    case OrderLogType.order_attachment_added:
      return (
        <>
          <p className="text-sm">
            <b>{log.user}</b> has{" "}
            <span className="text-green-500 font-semibold">Uploaded </span>{" "}
            Order attachments.
          </p>

          <div className="flex justify-end items-end py-1">
            <span className="text-xs italic">
              {moment(log.created_at).format("lll")}
            </span>
          </div>
        </>
      );
    case OrderLogType.raw_material_update: {
      const items: any[] = Array.isArray(log.params?.items)
        ? log.params.items
        : [];
      return (
        <>
          <p className="text-sm">
            <b>{log.user}</b> has{" "}
            <span className="text-yellow-500 font-semibold">Updated </span> raw
            materials.
          </p>
          <table className="border rounded w-full overflow-auto text-xs">
            <thead className="border rounded">
              <tr className="bg-gray-100">
                <th className="px-2 py-1 text-left">Item</th>
                <th className="px-2 py-1 text-left">Qty</th>
                <th className="px-2 py-1 text-left">Warehouse</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it, idx) => (
                <tr key={idx} className="border-b">
                  <td className="px-2 py-1">{it.name ?? "-"}</td>
                  <td className="px-2 py-1">{it.qty ?? "-"}</td>
                  <td className="px-2 py-1">{it.warehouse ?? "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>{" "}
          <div className="flex justify-end items-end py-1">
            <span className="text-xs italic">
              {moment(log.created_at).format("lll")}
            </span>
          </div>
        </>
      );
    }
    case OrderLogType.alloted_device_deleted: {
      const d = log.params?.device || {};
      return (
        <>
          <p className="text-sm">
            <b>{log.user}</b> has{" "}
            <span className="text-red-600 font-semibold">Removed </span> a device
            allocation.
          </p>
          <table className="border rounded w-full overflow-auto text-xs">
            <tbody>
              <tr className="border-b">
                <td className="px-2 py-1 font-semibold">Device Id</td>
                <td className="px-2 py-1">{d.device_id ?? "-"}</td>
              </tr>
              <tr className="border-b">
                <td className="px-2 py-1 font-semibold">Item</td>
                <td className="px-2 py-1">{d.item_name ?? "-"}</td>
              </tr>
              <tr className="border-b">
                <td className="px-2 py-1 font-semibold">Status</td>
                <td className="px-2 py-1">{d.status ?? "-"}</td>
              </tr>
              <tr className="border-b">
                <td className="px-2 py-1 font-semibold">Warehouse</td>
                <td className="px-2 py-1">{d.warehouse ?? "-"}</td>
              </tr>
            </tbody>
          </table>{" "}
          <div className="flex justify-end items-end py-1">
            <span className="text-xs italic">
              {moment(log.created_at).format("lll")}
            </span>
          </div>
        </>
      );
    }
    case OrderLogType.device_org_changed: {
      const pre = log.params?.pre_value || {};
      const next = log.params?.new_value || {};
      return (
        <>
          <p className="text-sm">
            <b>{log.user}</b>{" "}
            <span className="text-blue-600 font-semibold">Changed </span>{" "}
            organisation for device{" "}
            <b className="text-blue-500">{log.params?.device_id ?? "-"}</b>.
          </p>
          <table className="border rounded w-full overflow-auto text-xs">
            <thead className="border rounded">
              <tr>
                <th className="px-2 py-1 text-left">Field</th>
                <th className="px-2 py-1 text-left text-red-600">Previous</th>
                <th className="px-2 py-1 text-left text-green-600">New</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b">
                <td className="px-2 py-1 font-medium">Org Name</td>
                <td className="px-2 py-1 bg-red-50">{pre.org_name ?? "-"}</td>
                <td className="px-2 py-1 bg-green-50">{next.org_name ?? "-"}</td>
              </tr>
              <tr className="border-b">
                <td className="px-2 py-1 font-medium">Org Namespace</td>
                <td className="px-2 py-1 bg-red-50">
                  {pre.org_namespace ?? "-"}
                </td>
                <td className="px-2 py-1 bg-green-50">
                  {next.org_namespace ?? "-"}
                </td>
              </tr>
            </tbody>
          </table>{" "}
          <div className="flex justify-end items-end py-1">
            <span className="text-xs italic">
              {moment(log.created_at).format("lll")}
            </span>
          </div>
        </>
      );
    }
    case OrderLogType.subscription_plan_created:
    case OrderLogType.subscription_plan_updated: {
      const v = log.params?.new_value || {};
      const isCreate =
        log.log_type === OrderLogType.subscription_plan_created;
      return (
        <>
          <p className="text-sm">
            <b>{log.user}</b> has{" "}
            <span
              className={`font-semibold ${
                isCreate ? "text-green-600" : "text-yellow-500"
              }`}
            >
              {isCreate ? "Created" : "Updated"}{" "}
            </span>{" "}
            a subscription plan.
          </p>
          <table className="border rounded w-full overflow-auto text-xs">
            <tbody>
              <tr className="border-b">
                <td className="px-2 py-1 font-semibold">Renewal Fee</td>
                <td className="px-2 py-1">{v.renewal_fee ?? "-"}</td>
              </tr>
              <tr className="border-b">
                <td className="px-2 py-1 font-semibold">AMC</td>
                <td className="px-2 py-1">{v.amc ?? "-"}</td>
              </tr>
              <tr className="border-b">
                <td className="px-2 py-1 font-semibold">SIM Charges</td>
                <td className="px-2 py-1">{v.sim_charges ?? "-"}</td>
              </tr>
              <tr className="border-b">
                <td className="px-2 py-1 font-semibold">Start Date</td>
                <td className="px-2 py-1">{renderLogValue(v.start_date)}</td>
              </tr>
              <tr className="border-b">
                <td className="px-2 py-1 font-semibold">End Date</td>
                <td className="px-2 py-1">{renderLogValue(v.end_date)}</td>
              </tr>
            </tbody>
          </table>{" "}
          <div className="flex justify-end items-end py-1">
            <span className="text-xs italic">
              {moment(log.created_at).format("lll")}
            </span>
          </div>
        </>
      );
    }
    case OrderLogType.subscription_bill_created: {
      const v = log.params?.new_value || {};
      return (
        <>
          <p className="text-sm">
            <b>{log.user}</b> has{" "}
            <span className="text-green-600 font-semibold">Created </span> a
            subscription bill.
          </p>
          <table className="border rounded w-full overflow-auto text-xs">
            <tbody>
              <tr className="border-b">
                <td className="px-2 py-1 font-semibold">Bill Type</td>
                <td className="px-2 py-1">{v.bill_type ?? "-"}</td>
              </tr>
              <tr className="border-b">
                <td className="px-2 py-1 font-semibold">Amount</td>
                <td className="px-2 py-1">{v.amount ?? "-"}</td>
              </tr>
            </tbody>
          </table>{" "}
          <div className="flex justify-end items-end py-1">
            <span className="text-xs italic">
              {moment(log.created_at).format("lll")}
            </span>
          </div>
        </>
      );
    }
    default:
      return <>Not handled {log.log_type}</>;
  }
};
